// 숫자 포맷팅
export const formatNumber = (num) => {
  if (num === null || num === undefined) return '0';
  return new Intl.NumberFormat('ko-KR').format(num);
};

export const formatCurrency = (num) => {
  if (num === null || num === undefined) return '0원';
  return `${formatNumber(num)}원`;
};

export const formatPercent = (num, decimals = 2) => {
  if (num === null || num === undefined) return '0.00%';
  return `${Number(num).toFixed(decimals)}%`;
};

// 날짜 포맷팅
export const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

export const formatDateTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleString('ko-KR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const formatTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleTimeString('ko-KR', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

// 상태에 따른 색상 클래스
export const getStatusColor = (status) => {
  const statusColors = {
    success: 'text-green-600 bg-green-100',
    error: 'text-red-600 bg-red-100',
    warning: 'text-yellow-600 bg-yellow-100',
    info: 'text-blue-600 bg-blue-100',
    active: 'text-green-600 bg-green-100',
    inactive: 'text-gray-600 bg-gray-100',
  };
  return statusColors[status] || statusColors.info;
};

// 변화율에 따른 색상
export const getChangeColor = (change) => {
  if (change > 0) return 'text-red-600'; // 상승
  if (change < 0) return 'text-blue-600'; // 하락
  return 'text-gray-600'; // 보합
};

// 변화 아이콘
export const getChangeIcon = (change) => {
  if (change > 0) return '▲';
  if (change < 0) return '▼';
  return '━';
};

// 주문 타입 한글 변환
export const getOrderTypeText = (orderType) => {
  const types = {
    BUY: '매수',
    SELL: '매도',
    MARKET: '시장가',
    LIMIT: '지정가',
  };
  return types[orderType] || orderType;
};

// 주문 상태 한글 변환
export const getOrderStatusText = (status) => {
  const statusMap = {
    PENDING: '대기',
    COMPLETED: '체결',
    CANCELLED: '취소',
    FAILED: '실패',
  };
  return statusMap[status] || status;
};

// 신뢰도 색상
export const getConfidenceColor = (confidence) => {
  if (confidence >= 80) return 'text-green-600 bg-green-100';
  if (confidence >= 60) return 'text-yellow-600 bg-yellow-100';
  if (confidence >= 40) return 'text-orange-600 bg-orange-100';
  return 'text-red-600 bg-red-100';
};

// 추천 신호 색상
export const getRecommendationColor = (recommendation) => {
  const colors = {
    BUY: 'text-red-600 bg-red-100',
    SELL: 'text-blue-600 bg-blue-100', 
    HOLD: 'text-gray-600 bg-gray-100',
  };
  return colors[recommendation] || colors.HOLD;
};

// 추천 신호 텍스트
export const getRecommendationText = (recommendation) => {
  const texts = {
    BUY: '매수',
    SELL: '매도',
    HOLD: '보유',
  };
  return texts[recommendation] || recommendation;
};

// 디바운스
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// ✅ 인증 관련 에러 처리 함수 추가
export const handleAuthError = (error) => {
  console.log('🔍 handleAuthError called with:', error);
  console.log('🔍 error 전체 구조:', JSON.stringify(error, null, 2));
  console.log('🔍 error.response:', error.response);
  console.log('🔍 error.response?.data:', error.response?.data);
  console.log('🔍 error.response?.status:', error.response?.status);
  
  // 👀 원시 응답 데이터 확인
  if (error.response) {
    console.log('🔍 response 객체 키들:', Object.keys(error.response));
    console.log('🔍 response.data 타입:', typeof error.response.data);
    console.log('🔍 response.data stringify:', JSON.stringify(error.response.data));
  }
  
  // HTTP 403 Forbidden 에러에서 이메일 인증 필요 감지
  if (error.response?.status === 403) {
    // 🔥 수정: 백엔드에서 'message' 필드로 전달
    const message = error.response?.data?.message;
    console.log('🔍 403 에러 message:', message);
    console.log('🔍 message type:', typeof message);
    console.log('🔍 message?.type:', message?.type);
    
    // 이메일 인증이 필요한 경우
    if (typeof message === 'object' && message?.type === 'EMAIL_NOT_VERIFIED') {
      console.log('✅ EMAIL_NOT_VERIFIED 감지!');
      return {
        type: 'EMAIL_NOT_VERIFIED',
        message: message.message || '이메일 인증이 필요합니다',
        email: message.email,
        redirectTo: message.redirect_to || 'email_verification'
      };
    }
    
    // 일반적인 403 에러
    console.log('❌ 일반 403 에러로 처리');
    return {
      type: 'FORBIDDEN',
      message: typeof message === 'string' ? message : '접근이 거부되었습니다'
    };
  }
  
  // 일반적인 에러 처리
  console.log('❌ 일반 에러로 처리');
  return {
    type: 'GENERAL_ERROR',
    message: getErrorMessage(error)
  };
};

// 기존 getErrorMessage 함수 개선
export const getErrorMessage = (error) => {
  // 네트워크 에러
  if (!error.response) {
    return '네트워크 연결을 확인해주세요.';
  }
  
  // HTTP 상태별 기본 메시지
  const statusMessages = {
    400: '잘못된 요청입니다.',
    401: '인증이 필요합니다.',
    403: '접근이 거부되었습니다.',
    404: '요청한 리소스를 찾을 수 없습니다.',
    500: '서버 내부 오류가 발생했습니다.',
    502: '서버 게이트웨이 오류입니다.',
    503: '서비스를 사용할 수 없습니다.',
  };
  
  // 백엔드에서 제공하는 상세 에러 메시지 우선
  if (error.response?.data?.detail) {
    // detail이 객체인 경우 (이메일 인증 필요 등)
    if (typeof error.response.data.detail === 'object') {
      return error.response.data.detail.message || '오류가 발생했습니다.';
    }
    // detail이 문자열인 경우
    return error.response.data.detail;
  }
  
  if (error.response?.data?.message) {
    return error.response.data.message;
  }
  
  // HTTP 상태 코드 기반 메시지
  const status = error.response?.status;
  if (statusMessages[status]) {
    return statusMessages[status];
  }
  
  // 최후의 수단
  if (error.message) {
    return error.message;
  }
  
  return '알 수 없는 오류가 발생했습니다.';
};

export default {
  formatNumber,
  formatCurrency,
  formatPercent,
  formatDate,
  formatDateTime,
  formatTime,
  getStatusColor,
  getChangeColor,
  getChangeIcon,
  getOrderTypeText,
  getOrderStatusText,
  getConfidenceColor,
  getRecommendationColor,
  getRecommendationText,
  getErrorMessage,
  handleAuthError,
  debounce,
};